import React, { useState, useEffect } from "react";
import { ReactComponent as MaximizeIcon } from "../../assets/icons/svg/maximize_icon.svg";
import { DEFAULT_FONT_FAMILY } from "../../config/fontConfig";

interface MinimizedWorkspaceProps {
  feedbackName: string;
  previewImage: string;
  totalFeedbacks: number;
  onMaximize: () => void;
  onClose: () => void;
  onSaveDraft?: () => void;
}

const MinimizedWorkspace: React.FC<MinimizedWorkspaceProps> = ({
  feedbackName,
  previewImage,
  totalFeedbacks,
  onMaximize,
  onClose,
  onSaveDraft,
}) => {
  // Position state - starts at bottom-right corner
  const [position, setPosition] = useState({
    x: window.innerWidth - 360,
    y: window.innerHeight - 266,
  });

  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isHovered, setIsHovered] = useState(false);

  // Handle drag start
  const handleMouseDown = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest(".pip-action-btn")) return;

    setIsDragging(true);
    setDragOffset({
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    });
  };

  // Handle dragging
  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging) return;

    const newX = Math.max(
      0,
      Math.min(window.innerWidth - 300, e.clientX - dragOffset.x)
    );
    const newY = Math.max(
      0,
      Math.min(window.innerHeight - 180, e.clientY - dragOffset.y)
    );

    setPosition({ x: newX, y: newY });
  };

  // Handle drag end
  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDragging, dragOffset]);

  useEffect(() => {
    const handleResize = () => {
      setPosition((prev) => ({
        x: Math.min(prev.x, window.innerWidth - 300),
        y: Math.min(prev.y, window.innerHeight - 180),
      }));
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // ✅ FINAL: Save draft and close in one action
  const handleSaveDraft = async (e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (onSaveDraft) {
      // Save the draft directly (no confirmation popup)
      onSaveDraft();
      
      // Close the PiP after saving   
      onClose();
    }
  };

  // ❌ REMOVED: handleClose function
  // PiP can ONLY be closed by saving draft

  return (
    <div
      className="fixed bg-white rounded-[10px] shadow-lg border-[0.5px] border-[#B7BBCA] overflow-hidden transition-all duration-200 hover:shadow-xl z-[9999]"
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
        width: "340px",
        cursor: isDragging ? "grabbing" : "grab",
        userSelect: "none",
      }}
      onMouseDown={handleMouseDown}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Preview Image Section */}
      <div
        className="relative bg-gray-100 cursor-pointer group"
        style={{ height: "200px" }}
        onClick={onMaximize}
      >
        {previewImage ? (
          <img
            src={previewImage}
            alt={feedbackName}
            className="w-full h-full object-cover"
            draggable={false}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
            <svg
              className="w-12 h-12 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
          </div>
        )}

        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-60 transition-all flex items-center justify-center">
          <span className="text-white text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity bg-black bg-opacity-30 px-3 py-1 rounded">
             <MaximizeIcon className="w-8 h-8 2xl:w-8 2xl:h-8 3xl:w-10 3xl:h-10" />
          </span>
        </div>
      </div>
      
      {/* Header with controls */}
      <div className="bg-[#FFFFFF] px-3  flex items-center justify-between">
        <div className="flex items-center py-3 gap-2 flex-1 min-w-0">
          <span className="text-[#1E1F26] text-sm 4xl:text-[16px] font-medium  truncate"  style={{ fontFamily: DEFAULT_FONT_FAMILY }}>
            {feedbackName}
          </span>
        </div>

        {/* ✅ ONLY ACTION: Save Draft button (shows on hover) */}
        {/* ❌ NO CLOSE BUTTON - Can only close by saving */}
        <div className="flex items-center gap-1">
          {isHovered && onSaveDraft && (
            <button
              onClick={handleSaveDraft}
              className="pip-action-btn px-2 py-1 flex items-center  rounded-[6px]  transition-all border border-[#1E1F26]  text-[#1E1F26] text-[12px] 4xl:text-sm font-medium shadow-sm animate-in fade-in slide-in-from-right-2 duration-200"
              title="Save as draft and close"
               style={{ fontFamily: DEFAULT_FONT_FAMILY }}
            >
              <span>Save draft</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default MinimizedWorkspace;